import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAppSettings } from "../../../../components/providers/AppSettingsProvider";

interface DurationLimitModalProps {
    show: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    actionText?: string;
    onAction?: () => void;
    hideAction?: boolean;
}

export function DurationLimitModal({ 
    show, 
    onClose, 
    title, 
    description, 
    actionText, 
    onAction,
    hideAction
}: DurationLimitModalProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';

    if (typeof document === 'undefined') return null;

    const handleAction = () => {
        if (onAction) {
            onAction();
        } else {
            onClose();
        }
    };

    return createPortal(
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-[9999]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
                    <motion.div
                        className="absolute inset-x-0 bottom-0 backdrop-blur-2xl border-t border-white/20 rounded-t-[32px] px-6 pt-6 pb-8"
                        style={{
                            background: isDark
                                ? 'linear-gradient(to bottom, rgba(60, 136, 238, 0.95), rgba(107, 187, 252, 0.95))'
                                : 'linear-gradient(to bottom, rgba(200, 230, 255, 0.95), rgba(230, 245, 255, 0.95))',
                            zIndex: 9999,
                            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4rem)',
                        }}
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', stiffness: 260, damping: 25 }}
                    >
                        <div className={`mx-auto w-12 h-1.5 rounded-full mb-6 ${isDark ? 'bg-white/30' : 'bg-white/50'}`} />
                        <div className="text-center mb-6 px-4">
                            <h2 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                {title || t('whisper.durationLimit.title') || 'Time Limit Reached'}
                            </h2>
                            <p className={`text-sm leading-relaxed ${isDark ? 'text-white/90' : 'text-gray-700'}`}>
                                {description || t('whisper.quotaExceeded') || 'See Our Price Plans'}
                            </p>
                        </div>
                        {!hideAction && (
                            <button
                                onClick={handleAction}
                                className={`w-full py-4 px-6 rounded-2xl text-base font-semibold transition-all active:scale-[0.98] ${isDark
                                    ? 'bg-white text-blue-600 shadow-lg shadow-blue-500/20'
                                    : 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                                    }`}
                            >
                                {actionText || t('whisper.durationLimit.acknowledge') || 'OK'}
                            </button>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}
